import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogBackdrop,
  DialogPanel,
} from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import {
  ArrowLeftIcon,
  BuildingOffice2Icon,
  CheckIcon,
  ClipboardDocumentIcon,
  DocumentTextIcon,
  FolderIcon,
  HashtagIcon,
  PhoneIcon,
  QueueListIcon,
  RectangleGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Airtable from "airtable";
import {
  buildFieldSearchItems,
  buildProfileContentSearchItems,
  buildSectionSearchItems,
  companyProfileSearchShortcutLabel,
  filterAndRankSearchItems,
  groupSearchResults,
} from "../utils/companyProfileSearch.js";
import { loadCompanyGrantQaSearchIndex } from "../utils/loadCompanyGrantQaSearchIndex.js";
import { loadCompanyCallRecordingsSearchIndex } from "../utils/loadCompanyCallRecordingsSearchIndex.js";
import {
  excerptAroundMatch,
  loadCompanyGoogleSearchIndex,
} from "../utils/loadCompanyGoogleSearchIndex.js";
import { normalizeGoogleUrl } from "../utils/googleUrlUtils.js";
import { driveFolderLabel } from "../utils/buildPerfectDraftGoogleContext.js";
import {
  DriveFileContentPreview,
  DriveFileTypeIcon,
} from "./DriveFileTypeIcon.jsx";

const apiKey = process.env.REACT_APP_AIR_KEY;
const baseId = process.env.REACT_APP_AIR_BASEID;
const base = new Airtable({ apiKey }).base(baseId);

const TABS = [
  { id: "all", label: "All" },
  { id: "sections", label: "Sections" },
  { id: "fields", label: "Fields" },
  { id: "qa", label: "Grant Q&A" },
  { id: "calls", label: "Calls" },
  { id: "google-doc", label: "Google Doc" },
  { id: "google-folders", label: "Google Folders" },
];

const typeIcon = (type) => {
  if (type === "company") return BuildingOffice2Icon;
  if (type === "section") return RectangleGroupIcon;
  if (type === "field") return HashtagIcon;
  if (type === "grant") return QueueListIcon;
  if (type === "call") return PhoneIcon;
  if (type === "google-doc") return DocumentTextIcon;
  if (type === "drive-file") return FolderIcon;
  return DocumentTextIcon;
};

const shortcutLabel = companyProfileSearchShortcutLabel;

const typeLabel = (type) => {
  if (type === "company") return "Company";
  if (type === "section") return "Section";
  if (type === "field") return "Field";
  if (type === "content") return "Profile";
  if (type === "grant") return "Grant";
  if (type === "call") return "Call";
  if (type === "qa") return "Q&A";
  if (type === "google-doc") return "Google Doc";
  if (type === "drive-file") return "Drive file";
  return "Result";
};

/** Build expandable preview content for the hovered / highlighted result. */
const getItemPreview = (item) => {
  if (!item) return null;

  const blocks = [];

  if (item.type === "qa") {
    if (item.questionText) {
      blocks.push({ key: "question", label: "Question", text: item.questionText });
    }
    if (item.answerText) {
      blocks.push({ key: "answer", label: "Answer", text: item.answerText });
    } else {
      blocks.push({ key: "answer", label: "Answer", text: "Unanswered" });
    }
    if (item.grantName) {
      blocks.push({ key: "grant", label: "Grant", text: item.grantName });
    }
  } else if (item.type === "field") {
    blocks.push({
      key: "value",
      label: "Value",
      text: item.value?.trim() ? item.value : "Empty",
    });
  } else if (item.type === "call") {
    blocks.push({
      key: "content",
      label: "Summary / transcript",
      text: item.value?.trim() ? item.value : item.subtitle || "No preview available",
    });
  } else if (item.type === "google-doc") {
    const fullText = item.fullText || item.value || "";
    blocks.push({
      key: "doc",
      label: item.truncated ? "Full text (truncated)" : "Full text",
      text: fullText.trim() ? fullText : "Doc is empty or could not be loaded.",
    });
    if (item.url) {
      blocks.push({ key: "url", label: "Link", text: item.url });
    }
  } else if (item.type === "drive-file") {
    blocks.push({
      key: "name",
      label: "File name",
      text: item.title || "Untitled file",
    });
    if (item.mimeLabel || item.mimeType) {
      blocks.push({
        key: "type",
        label: "Type",
        text: item.mimeLabel || item.mimeType,
      });
    }
    if (item.modifiedTime) {
      try {
        blocks.push({
          key: "modified",
          label: "Modified",
          text: new Date(item.modifiedTime).toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          }),
        });
      } catch {
        /* ignore */
      }
    }
    if (item.url) {
      blocks.push({ key: "url", label: "Link", text: item.url });
    }
  } else if (item.type === "company") {
    blocks.push({
      key: "domain",
      label: "Domain",
      text: item.subtitle || item.companyRecord?.fields?.Domain || "No domain",
    });
  } else if (item.value?.trim()) {
    blocks.push({ key: "value", label: "Details", text: item.value });
  } else if (item.subtitle) {
    blocks.push({ key: "subtitle", label: "Details", text: item.subtitle });
  }

  const copyParts = blocks
    .filter(
      (b) =>
        b.key !== "url" &&
        b.text &&
        b.text !== "Empty" &&
        b.text !== "Unanswered" &&
        b.text !== "No preview available" &&
        b.text !== "Doc is empty or could not be loaded."
    )
    .map((b) => (blocks.length > 1 ? `${b.label}\n${b.text}` : b.text));

  const copyText =
    item.type === "google-doc"
      ? (item.fullText || item.value || "").trim() || item.title || ""
      : copyParts.join("\n\n") || item.title || "";

  return {
    title: item.title,
    type: item.type,
    typeLabel: typeLabel(item.type),
    blocks,
    copyText,
    openUrl:
      item.type === "google-doc" || item.type === "drive-file"
        ? item.url || null
        : null,
  };
};

const PreviewCopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  if (!text) return null;

  return (
    <button
      type="button"
      onMouseDown={(event) => event.preventDefault()}
      onClick={async (event) => {
        event.preventDefault();
        event.stopPropagation();
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        } catch {
          /* ignore */
        }
      }}
      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
    >
      {copied ? (
        <CheckIcon className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />
      ) : (
        <ClipboardDocumentIcon className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      {copied ? "Copied" : "Copy"}
    </button>
  );
};

const drivePreviewCache = new Map();

function driveResultRows(items) {
  const order = [];
  const buckets = new Map();
  for (const item of items) {
    const label = driveFolderLabel(item.folderPath);
    if (!buckets.has(label)) {
      buckets.set(label, []);
      order.push(label);
    }
    buckets.get(label).push(item);
  }
  const rows = [];
  for (const label of order) {
    rows.push({ kind: "folder", label });
    for (const item of buckets.get(label)) rows.push({ kind: "file", item });
  }
  return rows;
}

const DriveFilePreviewPanel = ({ item }) => {
  const cacheKey = item?.driveFileId || "";
  const cached = cacheKey ? drivePreviewCache.get(cacheKey) : null;
  const [state, setState] = useState(
    cached || { loading: Boolean(cacheKey), text: "", error: null, mimeType: item?.mimeType, name: item?.title }
  );

  useEffect(() => {
    if (!item?.driveFileId || !item?.companyId) {
      setState({ loading: false, text: "", error: null, mimeType: item?.mimeType, name: item?.title });
      return undefined;
    }

    const hit = drivePreviewCache.get(item.driveFileId);
    if (hit && !hit.loading) {
      setState(hit);
      return undefined;
    }

    let cancelled = false;
    setState({ loading: true, text: "", error: null, mimeType: item.mimeType, name: item.title });

    fetch("/api/companyGoogleContext", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        companyId: item.companyId,
        action: "drive-contents",
        fileIds: [item.driveFileId],
      }),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload.error || "Couldn’t load file preview.");
        }
        const file = payload.files?.[0];
        if (file?.error) throw new Error(file.error);
        return {
          loading: false,
          text: file?.text || "",
          error: null,
          mimeType: file?.mimeType || item.mimeType,
          name: file?.name || item.title,
        };
      })
      .then((next) => {
        if (cancelled) return;
        drivePreviewCache.set(item.driveFileId, next);
        setState(next);
      })
      .catch((error) => {
        if (cancelled) return;
        setState({
          loading: false,
          text: "",
          error: error.message || "Couldn’t load file preview.",
          mimeType: item.mimeType,
          name: item.title,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [item?.driveFileId, item?.companyId, item?.mimeType, item?.title]);

  const folder = driveFolderLabel(item?.folderPath);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start gap-3 border-b border-gray-100 px-4 py-3">
        <DriveFileTypeIcon mimeType={state.mimeType || item?.mimeType} name={item?.title} />
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {folder}
          </p>
          <p className="mt-1 text-sm font-semibold text-gray-900 break-words">
            {item?.title}
          </p>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 select-text">
        <DriveFileContentPreview
          loading={state.loading}
          text={state.text}
          error={state.error}
          mimeType={state.mimeType || item?.mimeType}
          name={state.name || item?.title}
        />
      </div>
      <div className="border-t border-gray-100 px-4 py-2.5">
        <p className="text-xs text-gray-400">
          {item?.url
            ? "Enter opens this file in Google Drive."
            : "Preview of the file in the company Drive folder."}
        </p>
      </div>
    </div>
  );
};

const ResultPreviewPanel = ({ item }) => {
  if (item?.type === "drive-file") {
    return <DriveFilePreviewPanel item={item} />;
  }

  const preview = getItemPreview(item);

  if (!preview) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 py-10 text-center">
        <p className="text-sm font-medium text-gray-900">Preview</p>
        <p className="mt-1 text-sm text-gray-500">
          Hover or arrow through results to expand details.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {preview.typeLabel}
          </p>
          <p className="mt-1 text-sm font-semibold text-gray-900 break-words">
            {preview.title}
          </p>
        </div>
        <PreviewCopyButton text={preview.copyText} />
      </div>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 select-text">
        {preview.blocks.length === 0 ? (
          <p className="text-sm text-gray-500">No additional details for this result.</p>
        ) : (
          preview.blocks.map((block) => (
            <div key={block.key} className="space-y-1.5">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {block.label}
              </p>
              <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2.5">
                <p className="whitespace-pre-wrap break-words text-sm text-gray-900">
                  {block.text}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="border-t border-gray-100 px-4 py-2.5">
        <p className="text-xs text-gray-400">
          {preview.openUrl
            ? "Select text to copy, or use Copy. Enter opens in Google."
            : "Select text to copy, or use Copy. Enter to open."}
        </p>
      </div>
    </div>
  );
};

const getCompanyLogo = (record) => {
  if (record?.fields?.LogoUrl) return record.fields.LogoUrl;
  const domain = record?.fields?.Domain;
  if (!domain) return null;
  const normalized = String(domain)
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/\/$/, "")
    .split("/")[0];
  return `https://img.logo.dev/${normalized}?token=pk_acnHOju_S3KawCcUE6Ek2Q`;
};

/**
 * Company profile search palette.
 *
 * - Scoped: pass `company` and search fields / Grant Q&A.
 * - Global (`allowCompanyPick`): first pick a company, then search within it.
 *   Context `company` is used as the initial scope when present.
 */
const CompanyProfileSearchPalette = ({
  isOpen,
  onClose,
  company: contextCompany = null,
  formData = null,
  sectionNavItems = [],
  allowCompanyPick = false,
  onSelect,
}) => {
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [scopedCompany, setScopedCompany] = useState(null);
  const [companyResults, setCompanyResults] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [qaItems, setQaItems] = useState([]);
  const [qaLoading, setQaLoading] = useState(false);
  const [qaError, setQaError] = useState(null);
  const [callItems, setCallItems] = useState([]);
  const [callLoading, setCallLoading] = useState(false);
  const [callError, setCallError] = useState(null);
  const [googleItems, setGoogleItems] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleDocError, setGoogleDocError] = useState(null);
  const [googleDriveError, setGoogleDriveError] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const optionsRef = useRef(null);
  const inputRef = useRef(null);

  const pickingCompany = allowCompanyPick && !scopedCompany?.id;
  const includeCallRecordings = sectionNavItems.some(
    (item) => item.id === "company-call-recordings"
  );
  const visibleTabs = useMemo(
    () =>
      includeCallRecordings
        ? TABS
        : TABS.filter((tab) => tab.id !== "calls"),
    [includeCallRecordings]
  );

  // Initialize / reset scope when opened
  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setActiveTab("all");
      setHighlightedIndex(0);
      setQaError(null);
      setCallError(null);
      setGoogleDocError(null);
      setGoogleDriveError(null);
      setCompanyResults([]);
      setScopedCompany(null);
      setQaItems([]);
      setCallItems([]);
      setGoogleItems([]);
      return;
    }

    if (contextCompany?.id) {
      setScopedCompany(contextCompany);
    } else if (allowCompanyPick) {
      setScopedCompany(null);
    } else {
      setScopedCompany(contextCompany);
    }
  }, [isOpen, contextCompany, allowCompanyPick]);

  // Company Airtable search (step 1)
  useEffect(() => {
    if (!isOpen || !pickingCompany) {
      setCompanyResults([]);
      setCompanyLoading(false);
      return undefined;
    }

    const q = query.trim();
    if (q.length < 2) {
      setCompanyResults([]);
      setCompanyLoading(false);
      return undefined;
    }

    let cancelled = false;
    setCompanyLoading(true);
    const searchTerm = q.toLowerCase().replace(/"/g, '\\"');

    const timeoutId = window.setTimeout(() => {
      base("companies")
        .select({
          filterByFormula: `OR(
            FIND(LOWER("${searchTerm}"), LOWER({Name})),
            FIND(LOWER("${searchTerm}"), LOWER({Domain}))
          )`,
          maxRecords: 12,
        })
        .all()
        .then((records) => {
          if (cancelled) return;
          setCompanyResults(Array.isArray(records) ? records : []);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error("[CompanyProfileSearchPalette] company search failed:", err);
          setCompanyResults([]);
        })
        .finally(() => {
          if (!cancelled) setCompanyLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [isOpen, pickingCompany, query]);

  // Grant Q&A index once scoped (step 2)
  useEffect(() => {
    if (!isOpen || pickingCompany || !scopedCompany?.id) {
      setQaItems([]);
      return undefined;
    }

    let cancelled = false;
    setQaLoading(true);
    setQaError(null);
    loadCompanyGrantQaSearchIndex(scopedCompany)
      .then((data) => {
        if (cancelled) return;
        setQaItems(data.items || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[CompanyProfileSearchPalette] Q&A index failed:", err);
        setQaError("Couldn’t load Grant Q&A results.");
        setQaItems([]);
      })
      .finally(() => {
        if (!cancelled) setQaLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, pickingCompany, scopedCompany]);

  // Call recordings keyword index (admin section only)
  useEffect(() => {
    if (!isOpen || pickingCompany || !scopedCompany?.id || !includeCallRecordings) {
      setCallItems([]);
      setCallLoading(false);
      setCallError(null);
      return undefined;
    }

    let cancelled = false;
    setCallLoading(true);
    setCallError(null);
    loadCompanyCallRecordingsSearchIndex(scopedCompany)
      .then((data) => {
        if (cancelled) return;
        setCallItems(data.items || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(
          "[CompanyProfileSearchPalette] call recordings index failed:",
          err
        );
        setCallError("Couldn’t load call recordings.");
        setCallItems([]);
      })
      .finally(() => {
        if (!cancelled) setCallLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, pickingCompany, scopedCompany, includeCallRecordings]);

  // Company Google Doc + Drive folder index
  useEffect(() => {
    if (!isOpen || pickingCompany || !scopedCompany?.id) {
      setGoogleItems([]);
      setGoogleLoading(false);
      setGoogleDocError(null);
      setGoogleDriveError(null);
      return undefined;
    }

    let cancelled = false;
    setGoogleLoading(true);
    setGoogleDocError(null);
    setGoogleDriveError(null);
    loadCompanyGoogleSearchIndex(scopedCompany)
      .then((data) => {
        if (cancelled) return;
        setGoogleItems(data.items || []);
        setGoogleDocError(data.docError || null);
        setGoogleDriveError(data.driveError || null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(
          "[CompanyProfileSearchPalette] Google context index failed:",
          err
        );
        setGoogleDocError("Couldn’t load Google Doc.");
        setGoogleDriveError("Couldn’t load Google Drive folder.");
        setGoogleItems([]);
      })
      .finally(() => {
        if (!cancelled) setGoogleLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, pickingCompany, scopedCompany]);

  const localItems = useMemo(() => {
    if (pickingCompany || !scopedCompany) return [];
    // Prefer live context formData only when scoped company matches context
    const liveForm =
      formData && scopedCompany?.id === contextCompany?.id ? formData : null;
    return [
      ...buildSectionSearchItems(sectionNavItems),
      ...buildFieldSearchItems(scopedCompany, liveForm),
      ...buildProfileContentSearchItems(scopedCompany, liveForm),
    ];
  }, [
    pickingCompany,
    scopedCompany,
    formData,
    contextCompany,
    sectionNavItems,
  ]);

  const companyPickItems = useMemo(() => {
    if (!pickingCompany) return [];
    return companyResults.map((record) => ({
      id: `company-${record.id}`,
      resultKey: `company-${record.id}`,
      type: "company",
      companyRecord: record,
      title: record.fields?.Name || "Untitled company",
      subtitle: record.fields?.Domain || "No domain",
      logoUrl: getCompanyLogo(record),
      category: "Companies",
    }));
  }, [pickingCompany, companyResults]);

  const allIndexed = useMemo(
    () => [...localItems, ...qaItems, ...callItems, ...googleItems],
    [localItems, qaItems, callItems, googleItems]
  );

  const filtered = useMemo(() => {
    if (pickingCompany) return companyPickItems;

    let pool = allIndexed;
    if (activeTab === "sections") {
      pool = allIndexed.filter((i) => i.type === "section");
    } else if (activeTab === "fields") {
      pool = allIndexed.filter((i) => i.type === "field" || i.type === "content");
    } else if (activeTab === "qa") {
      pool = allIndexed.filter((i) => i.type === "grant" || i.type === "qa");
    } else if (activeTab === "calls") {
      pool = allIndexed.filter((i) => i.type === "call");
    } else if (activeTab === "google-doc") {
      pool = allIndexed.filter((i) => i.type === "google-doc");
    } else if (activeTab === "google-folders") {
      pool = allIndexed.filter((i) => i.type === "drive-file");
    }

    const q = query.trim();
    let results;
    if (!q) {
      if (activeTab === "all" || activeTab === "sections") {
        results = pool.filter((i) => i.type === "section").slice(0, 12);
      } else if (activeTab === "fields") {
        results = pool.filter((i) => i.type === "field").slice(0, 12);
      } else if (activeTab === "qa") {
        results = pool.filter((i) => i.type === "grant").slice(0, 16);
      } else if (activeTab === "calls") {
        results = pool.filter((i) => i.type === "call").slice(0, 16);
      } else if (activeTab === "google-doc") {
        results = pool.filter((i) => i.type === "google-doc").slice(0, 4);
      } else if (activeTab === "google-folders") {
        results = pool.filter((i) => i.type === "drive-file").slice(0, 40);
      } else {
        results = filterAndRankSearchItems(pool, query, { limit: 48 });
      }
    } else {
      results = filterAndRankSearchItems(pool, query, { limit: 48 });
    }

    // Surface match context for the master doc while searching.
    if (q) {
      const needle = q.toLowerCase();
      return results.map((item) => {
        if (item.type === "google-doc") {
          const excerpt = excerptAroundMatch(item.fullText || item.value, q);
          return excerpt ? { ...item, subtitle: excerpt } : item;
        }
        if (
          (item.type === "field" || item.type === "content") &&
          item.value &&
          !String(item.title || "").toLowerCase().includes(needle)
        ) {
          const excerpt = excerptAroundMatch(item.value, q);
          return excerpt ? { ...item, subtitle: excerpt } : item;
        }
        return item;
      });
    }

    return results;
  }, [pickingCompany, companyPickItems, allIndexed, activeTab, query]);

  const groups = useMemo(() => {
    if (pickingCompany) {
      return filtered.length ? [["Companies", filtered]] : [];
    }
    return groupSearchResults(filtered);
  }, [pickingCompany, filtered]);

  const flatResults = useMemo(
    () => groups.flatMap(([, items]) => items),
    [groups]
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query, activeTab, filtered.length, pickingCompany, scopedCompany?.id]);

  useEffect(() => {
    if (activeTab === "calls" && !includeCallRecordings) {
      setActiveTab("all");
    }
  }, [activeTab, includeCallRecordings]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (allowCompanyPick && scopedCompany && contextCompany?.id !== scopedCompany.id) {
          // Back to company pick when user switched away from context
          setScopedCompany(null);
          setQuery("");
          return;
        }
        if (allowCompanyPick && scopedCompany && !contextCompany?.id) {
          setScopedCompany(null);
          setQuery("");
          return;
        }
        onClose();
        return;
      }

      // Empty query + Backspace while scoped with company pick → clear company
      if (
        event.key === "Backspace" &&
        allowCompanyPick &&
        scopedCompany &&
        !query &&
        document.activeElement === inputRef.current
      ) {
        event.preventDefault();
        setScopedCompany(null);
        setActiveTab("all");
        return;
      }

      if (
        (event.key === "ArrowDown" || event.key === "ArrowUp") &&
        optionsRef.current
      ) {
        const options = Array.from(
          optionsRef.current.querySelectorAll('[role="option"]')
        );
        if (options.length === 0) return;
        event.preventDefault();
        setHighlightedIndex((prev) => {
          let next;
          if (event.key === "ArrowDown") {
            next = prev < options.length - 1 ? prev + 1 : 0;
          } else {
            next = prev > 0 ? prev - 1 : options.length - 1;
          }
          requestAnimationFrame(() => {
            options[next]?.focus();
          });
          return next;
        });
      }
    };

    document.addEventListener("keydown", handleKeyDown, { capture: true });
    return () =>
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
  }, [
    isOpen,
    onClose,
    allowCompanyPick,
    scopedCompany,
    contextCompany,
    query,
  ]);

  const clearScopedCompany = useCallback(() => {
    setScopedCompany(null);
    setQuery("");
    setActiveTab("all");
    setQaItems([]);
    setGoogleItems([]);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const handleSelect = useCallback(
    (item) => {
      if (!item) return;

      if (item.type === "company" && item.companyRecord) {
        setScopedCompany(item.companyRecord);
        setQuery("");
        setActiveTab("all");
        requestAnimationFrame(() => inputRef.current?.focus());
        return;
      }

      if (item.type === "google-doc" || item.type === "drive-file") {
        const rawUrl = item.url;
        const url = rawUrl ? normalizeGoogleUrl(rawUrl) || rawUrl : null;
        if (url && typeof window !== "undefined") {
          window.open(url, "_blank", "noopener,noreferrer");
        }
        onClose();
        setQuery("");
        return;
      }

      onSelect?.(item, scopedCompany);
      onClose();
      setQuery("");
    },
    [onClose, onSelect, scopedCompany]
  );

  const placeholder = pickingCompany
    ? "Search companies by name or domain…"
    : activeTab === "google-doc"
      ? "Search the company Google Doc…"
      : activeTab === "google-folders"
        ? "Search Drive folder files by name…"
        : "Search fields, interests, research, earnings estimate…";

  const emptyTitle = pickingCompany
    ? query.trim().length < 2
      ? "Choose a company"
      : companyLoading
        ? "Searching…"
        : "No companies found"
    : googleLoading &&
        (activeTab === "google-doc" || activeTab === "google-folders")
      ? "Loading Google…"
      : activeTab === "google-doc" && googleDocError && !query.trim()
        ? "Google Doc unavailable"
        : activeTab === "google-folders" && googleDriveError && !query.trim()
          ? "Google Folders unavailable"
          : query.trim()
            ? "No matches"
            : "Search this company";

  const emptyBody = pickingCompany
    ? query.trim().length < 2
      ? "Type at least 2 characters to find a company, then search its profile and Grant Q&A."
      : companyLoading
        ? "Looking up companies…"
        : "Try another name or domain."
    : googleLoading &&
        (activeTab === "google-doc" || activeTab === "google-folders")
      ? "Fetching company Google Doc and Drive folder…"
      : activeTab === "google-doc" && googleDocError && !query.trim()
        ? googleDocError
        : activeTab === "google-folders" && googleDriveError && !query.trim()
          ? googleDriveError
          : activeTab === "google-doc"
            ? query.trim()
              ? "No text in the master Google Doc matched that search."
              : "Open this tab to preview the full company Google Doc."
            : activeTab === "google-folders"
              ? query.trim()
                ? "No Drive files matched that name."
                : "Files from the company Google Drive folder will appear here."
              : query.trim()
                ? "Try a field, interest, research phrase, earnings estimate detail, grant question, or call keyword."
                : `Type to search the company profile, Grant Q&A, calls, and Google — or pick a section. ${shortcutLabel}`;

  const previewItem =
    flatResults.length > 0
      ? flatResults[Math.min(highlightedIndex, flatResults.length - 1)] || null
      : null;

  return (
    <Dialog
      className="relative z-50"
      open={isOpen}
      onClose={() => {
        onClose();
        setQuery("");
      }}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/20 transition-opacity data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
        <DialogPanel
          transition
          className="mx-auto max-w-2xl transform overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl outline-none transition-all data-closed:scale-95 data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in md:max-w-4xl"
        >
          <Combobox onChange={handleSelect}>
            {scopedCompany && allowCompanyPick && (
              <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5">
                <button
                  type="button"
                  onClick={clearScopedCompany}
                  className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900"
                >
                  <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Companies
                </button>
                <span className="text-gray-300" aria-hidden="true">
                  /
                </span>
                <span className="inline-flex min-w-0 items-center gap-2 rounded-full bg-gray-100 py-1 pl-1 pr-2.5 text-xs font-medium text-gray-800">
                  {getCompanyLogo(scopedCompany) ? (
                    <img
                      src={getCompanyLogo(scopedCompany)}
                      alt=""
                      className="h-5 w-5 rounded-full bg-white object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <BuildingOffice2Icon className="h-4 w-4 text-gray-400" />
                  )}
                  <span className="truncate">
                    {scopedCompany.fields?.Name || "Company"}
                  </span>
                  <button
                    type="button"
                    onClick={clearScopedCompany}
                    className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700"
                    aria-label="Clear company"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </span>
              </div>
            )}

            <div className="grid grid-cols-1">
              <ComboboxInput
                ref={inputRef}
                autoFocus
                className="col-start-1 row-start-1 h-12 w-full border-0 bg-transparent pl-11 pr-4 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:outline-none focus:ring-0"
                placeholder={placeholder}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <MagnifyingGlassIcon
                className="pointer-events-none col-start-1 row-start-1 ml-4 size-5 self-center text-gray-400"
                aria-hidden="true"
              />
            </div>

            {!pickingCompany && (
              <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-4">
                <div className="flex gap-1 overflow-x-auto">
                  {visibleTabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`whitespace-nowrap rounded-t-md px-3 py-2.5 text-xs font-medium transition-colors focus:outline-none ${
                        activeTab === tab.id
                          ? "-mb-px border-b-2 border-gray-900 bg-gray-50 text-gray-900"
                          : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <span className="hidden shrink-0 text-xs text-gray-400 sm:inline">
                  {shortcutLabel}
                </span>
              </div>
            )}

            {pickingCompany && (
              <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
                <p className="text-xs text-gray-500">
                  Step 1 — pick a company to search its profile
                </p>
                <span className="hidden text-xs text-gray-400 sm:inline">
                  {shortcutLabel}
                </span>
              </div>
            )}

            {qaLoading && !pickingCompany && (activeTab === "all" || activeTab === "qa") && (
              <div className="border-b border-gray-50 px-4 py-2 text-xs text-gray-400">
                Loading Grant Q&A…
              </div>
            )}
            {callLoading &&
              !pickingCompany &&
              includeCallRecordings &&
              (activeTab === "all" || activeTab === "calls") && (
                <div className="border-b border-gray-50 px-4 py-2 text-xs text-gray-400">
                  Loading call recordings…
                </div>
              )}
            {qaError && !pickingCompany && (
              <div className="border-b border-gray-50 px-4 py-2 text-xs text-red-600">
                {qaError}
              </div>
            )}
            {callError && !pickingCompany && (
              <div className="border-b border-gray-50 px-4 py-2 text-xs text-red-600">
                {callError}
              </div>
            )}
            {googleLoading &&
              !pickingCompany &&
              (activeTab === "all" ||
                activeTab === "google-doc" ||
                activeTab === "google-folders") && (
                <div className="border-b border-gray-50 px-4 py-2 text-xs text-gray-400">
                  Loading Google Doc & Drive folder…
                </div>
              )}
            {googleDocError &&
              !pickingCompany &&
              (activeTab === "all" || activeTab === "google-doc") && (
                <div className="border-b border-gray-50 px-4 py-2 text-xs text-amber-600">
                  Google Doc: {googleDocError}
                </div>
              )}
            {googleDriveError &&
              !pickingCompany &&
              (activeTab === "all" || activeTab === "google-folders") && (
                <div className="border-b border-gray-50 px-4 py-2 text-xs text-amber-600">
                  Google Folders: {googleDriveError}
                </div>
              )}

            {filtered.length > 0 ? (
              <div className="flex min-h-0 border-t border-gray-100">
                <div ref={optionsRef} className="min-w-0 flex-1 md:max-w-[55%]">
                  <ComboboxOptions
                    static
                    as="ul"
                    className="max-h-80 space-y-2 overflow-y-auto scroll-pb-2 scroll-pt-11 pb-2 md:max-h-[28rem]"
                  >
                    {(() => {
                      let flatIndex = 0;
                      return groups.map(([category, items]) => (
                        <li key={category}>
                          <h2 className="bg-gray-50 px-4 py-2.5 text-xs font-semibold text-gray-900">
                            {category}
                          </h2>
                          <ul className="mt-1 text-sm text-gray-800">
                            {(category === "Google Folders" ? driveResultRows(items) : items.map((item) => ({ kind: "file", item }))).map((row) => {
                              if (row.kind === "folder") {
                                return (
                                  <li
                                    key={`folder-${row.label}`}
                                    className="px-4 pb-1 pt-3 text-xs font-medium text-gray-500"
                                  >
                                    {row.label}
                                  </li>
                                );
                              }
                              const item = row.item;
                              const optionIndex = flatIndex++;
                              const isHighlighted = optionIndex === highlightedIndex;
                              const Icon = typeIcon(item.type);
                              return (
                                <ComboboxOption
                                  key={item.resultKey}
                                  value={item}
                                  onMouseEnter={() => setHighlightedIndex(optionIndex)}
                                  className={`group flex cursor-pointer items-start gap-3 rounded-md px-4 py-2.5 select-none transition-colors focus:outline-none ${
                                    isHighlighted
                                      ? "bg-gray-100 text-gray-900"
                                      : "hover:bg-gray-50 data-focus:bg-gray-50"
                                  }`}
                                >
                                  {item.type === "company" && item.logoUrl ? (
                                    <img
                                      src={item.logoUrl}
                                      alt=""
                                      className="mt-0.5 size-5 shrink-0 rounded-sm object-contain"
                                      onError={(e) => {
                                        e.currentTarget.style.display = "none";
                                      }}
                                    />
                                  ) : item.type === "drive-file" ? (
                                    <DriveFileTypeIcon
                                      mimeType={item.mimeType}
                                      name={item.title}
                                      className="mt-0.5"
                                    />
                                  ) : (
                                    <Icon
                                      className={`mt-0.5 size-5 shrink-0 ${
                                        isHighlighted
                                          ? "text-gray-700"
                                          : "text-gray-400"
                                      }`}
                                      aria-hidden="true"
                                    />
                                  )}
                                  <span className="min-w-0 flex-auto">
                                    <span className="block truncate font-medium text-gray-900">
                                      {item.title}
                                    </span>
                                    {(item.type === "drive-file" ? item.detail : item.subtitle) && (
                                      <span className="mt-0.5 block truncate text-xs text-gray-500">
                                        {item.type === "drive-file" ? item.detail : item.subtitle}
                                      </span>
                                    )}
                                  </span>
                                  {item.type !== "drive-file" && (
                                    <span className="mt-0.5 shrink-0 text-xs text-gray-400">
                                      {typeLabel(item.type)}
                                    </span>
                                  )}
                                </ComboboxOption>
                              );
                            })}
                          </ul>
                        </li>
                      ));
                    })()}
                  </ComboboxOptions>
                </div>

                <aside className="hidden min-h-[20rem] w-[45%] shrink-0 border-l border-gray-100 bg-white md:block">
                  <div className="h-full max-h-80 md:max-h-[28rem]">
                    <ResultPreviewPanel item={previewItem} />
                  </div>
                </aside>
              </div>
            ) : (
              <div className="px-6 py-14 text-center sm:px-14">
                <MagnifyingGlassIcon
                  className="mx-auto size-6 text-gray-400"
                  aria-hidden="true"
                />
                <p className="mt-4 text-sm font-medium text-gray-900">
                  {emptyTitle}
                </p>
                <p className="mt-1 text-sm text-gray-500">{emptyBody}</p>
              </div>
            )}
          </Combobox>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default CompanyProfileSearchPalette;
